import React from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  CircularProgress,
  Link as MuiLink,
  Typography,
} from '@mui/material';

import FailureAlert from '~/components/Common/Elements/FailureAlert';

import { useGetAccountDetails } from '~/hooks/apis/contracting/useGetAccountDetails';
import { useGetContractingAccountId } from '~/hooks/apis/contracting/useGetContractingAccountId';
import { useGetAccount } from '~/hooks/apis/offers/useGetAccount';

import AssetFieldGrid from '../../Assets/AssetFieldGrid';
import type { AssetField } from '../../Assets/AssetFieldGrid';
import AccountTabs from '../AccountTabs';
import ContactMethodLink from '../Contacts/ContactMethodLink';
import { accountLoadErrorReason } from '../accountLoadErrorReason';
import { accountPath } from '../accountRelatedModel';
import { useAccountOfferId } from '../useAccountOfferId';

import { toDetailFieldView } from './accountDetailsFields';
import { ACCOUNT_DETAILS_LAYOUT, type AccountDetailsField } from './accountDetailsLayout';
import type { AccountDetails } from './types';

function toGridField(field: AccountDetailsField, details: AccountDetails, offerId: string | undefined): AssetField {
  const { accountId: linkedAccountId, phone, ...view } = toDetailFieldView(details, field);
  if (phone) {
    return { ...view, value: <ContactMethodLink kind="phone" value={phone} /> };
  }
  if (!linkedAccountId) {
    return view;
  }
  return {
    ...view,
    value: (
      <MuiLink component={Link} to={accountPath(linkedAccountId, offerId)} underline="hover" variant="body2">
        {view.value}
      </MuiLink>
    ),
  };
}

export default function AccountDetailsPage() {
  const { id_account: accountId } = useParams<{ id_account: string }>();
  const offerId = useAccountOfferId();
  const { data: account } = useGetAccount(accountId);
  const idQuery = useGetContractingAccountId(accountId);
  const detailsQuery = useGetAccountDetails(idQuery.data);
  const details = detailsQuery.data;
  const failedQuery = idQuery.error ? idQuery : detailsQuery;
  const error = failedQuery.error;
  const isLoading = idQuery.isLoading || detailsQuery.isLoading;

  return (
    <>
      <Helmet>
        <title>Details | {account?.name || accountId}</title>
      </Helmet>

      <AccountTabs accountId={accountId} activeTab="details" />

      <Typography variant="h4" sx={{ mb: 3 }}>
        Details
      </Typography>

      {isLoading ? (
        <CircularProgress size={24} />
      ) : error ? (
        <FailureAlert
          message={`Could not load the Account details: ${accountLoadErrorReason(error)}.`}
          cause={error}
          onRetry={() => void failedQuery.refetch()}
          retrying={failedQuery.isFetching}
        />
      ) : details ? (
        ACCOUNT_DETAILS_LAYOUT.map((section) => (
          <Accordion key={section.title} defaultExpanded disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">{section.title}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <AssetFieldGrid
                left={section.left.map((field) => toGridField(field, details, offerId))}
                right={section.right.map((field) => toGridField(field, details, offerId))}
              />
            </AccordionDetails>
          </Accordion>
        ))
      ) : null}
    </>
  );
}
